import { FlashList } from '@shopify/flash-list';
import { Column, Row } from 'app/design/primitives/box';
import { createTheme as createDSTheme } from 'app/shopify';
import { PCImage } from 'app/design/primitives/image';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { localeAwareFormatDistance } from 'app/lib/date';
import { useOneListingLenders } from 'app/network/listing';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import type { Listing } from 'app/types/listing';
import type { NavigationScreen, SpecificNavigationProp } from 'app/types/navigation';
import { usePageTitle } from 'app/utils/web-helpers';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
const theme = createDSTheme();

const LenderCard = ({
  lender,
  nav,
}: {
  lender: Listing['lender'];
  nav: SpecificNavigationProp<'listing-activity'>;
}) => {
  const { t } = useTranslation('listing-activity');
  const hasAcceptedLoan = lender?.hasAcceptedLoan ?? false;

  const imageSource = lender?.picture ? { uri: lender.picture } : undefined;

  if (!lender) {
    return null;
  }

  return (
    <Pressable onPress={() => navigateToUserProfile(nav, lender.id)}>
      <Row
        alignItems="center"
        gap="spacing.general.x4"
        marginTop="spacing.general.x4"
        backgroundColor={hasAcceptedLoan ? 'surface.success.subtle' : 'surface.inverse.tertiary-neutral'}
        padding="spacing.general.x4"
        borderRadius="radius.lg"
      >
        <PCImage height={80} width={80} source={imageSource} style={{ borderRadius: 60 }} useOwnCache />
        <Column gap="spacing.general.x0,5" flexShrink={1}>
          <Column>
            <Text
              variant="bodyBase"
              fontSize={16}
              fontWeight="bold"
              color={hasAcceptedLoan ? 'surface.success.default' : undefined}
              marginRight="spacing.general.x1"
            >
              {lender.name}
            </Text>
            <Text variant="bodyBase" fontWeight="bold" fontSize={16} style={{ color: theme.colors['icon.primary'] }}>
              {hasAcceptedLoan ? t('hasLent') : t('leaveAMessage')}
            </Text>
          </Column>

          <Row>
            {lender.city || lender.distance !== undefined ? (
              <Text fontSize={14} flexShrink={1} style={{ color: theme.colors['icon.primary'] }}>
                {[lender.city, lender.distance !== undefined ? `${(lender.distance / 1000).toFixed(0)} km` : undefined]
                  .filter(Boolean)
                  .join(' - ')}
              </Text>
            ) : null}
          </Row>

          <Text fontSize={14} flexShrink={1} style={{ color: theme.colors['text.placeholder'] }}>
            {lender.lastMessageDate ? localeAwareFormatDistance(new Date(lender.lastMessageDate), new Date()) : null}
          </Text>
        </Column>
      </Row>
    </Pressable>
  );
};

const ListingActivityHeaderTitle = () => {
  const { t } = useTranslation('listing-activity');
  return <Text variant="bodyXLargeMedium">{t('headerTitle')}</Text>;
};

const ListingActivityHeaderTitleRenderer = () => <ListingActivityHeaderTitle />;

export const LenderParticipateScreen: NavigationScreen<'listing-activity'> = ({ navigation, route }) => {
  const { listingId, isLended } = route.params;
  const { data: lenders } = useOneListingLenders(listingId);
  const { t } = useTranslation('listing-activity');

  const setPageTitle = usePageTitle('listing-activity');
  useEffect(() => {
    setPageTitle('headerTitle');
  }, [setPageTitle]);

  useNavigationOptions(navigation, {
    headerTitle: ListingActivityHeaderTitleRenderer,
  });

  const renderItem = ({ item }: { item: Listing['lender'] }) =>
    item ? <LenderCard lender={item} nav={navigation} /> : null;

  return (
    <Page>
      <Text variant="headerXSmall" fontWeight="bold">
        {isLended
          ? t('wantedToHelp', { count: lenders?.length ?? 0 })
          : t('wantToHelp', { count: lenders?.length ?? 0 })}
      </Text>

      <FlashList data={lenders} renderItem={renderItem} keyExtractor={(item, index) => item?.id ?? String(index)} />
    </Page>
  );
};
